import React, { memo, useState } from "react";
import classNames from "classnames";
import { DateRangePreset } from "../types";

interface Props {
  preset: DateRangePreset | null;
  startDate: string | null;
  endDate: string | null;
  onPresetChange: (preset: DateRangePreset) => void;
  onCustomRange: (start: string, end: string) => void;
}

const PRESETS: { label: string; value: DateRangePreset }[] = [
  { label: "This Month", value: "this_month" },
  { label: "This Quarter", value: "this_quarter" },
  { label: "This Financial Year", value: "this_financial_year" },
  { label: "Last Month", value: "last_month" },
  { label: "Last Quarter", value: "last_quarter" },
  { label: "Last Financial Year", value: "last_financial_year" },
  { label: "Month to Date", value: "month_to_date" },
  { label: "Quarter to Date", value: "quarter_to_date" },
  { label: "Year to Date", value: "year_to_date" },
];

const DateRangeFilter = ({ preset, startDate, endDate, onPresetChange, onCustomRange }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customStart, setCustomStart] = useState(startDate ?? "");
  const [customEnd, setCustomEnd] = useState(endDate ?? "");
  const [showCustom, setShowCustom] = useState(false);

  const selectedLabel =
    PRESETS.find((p) => p.value === preset)?.label ??
    (startDate && endDate ? `${startDate} – ${endDate}` : "This Financial Year");

  return (
    <div className="relative inline-block mr-3">
      <button
        type="button"
        className="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-black-20 rounded text-13 hover:border-primary"
        style={{ width: 200 }}
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className="truncate text-left" style={{ minWidth: 0 }}>{selectedLabel}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-black-20 rounded shadow-md z-50" style={{ width: 200 }}>
          {PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              className={classNames(
                "block w-full text-left px-4 py-2 text-13 hover:bg-black-5",
                { "font-semibold text-primary": preset === p.value }
              )}
              onClick={() => {
                onPresetChange(p.value);
                setShowCustom(false);
                setIsOpen(false);
              }}
            >
              {p.label}
            </button>
          ))}
          <div className="border-t border-black-10">
            <button
              type="button"
              className="block w-full text-left px-4 py-2 text-13 hover:bg-black-5"
              onClick={() => setShowCustom((v) => !v)}
            >
              Custom date range…
            </button>
            {showCustom && (
              <div className="px-4 pb-3">
                <label className="text-11 text-black-40 block mb-1">From</label>
                <input
                  type="date"
                  className="form-control mb-2"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
                <label className="text-11 text-black-40 block mb-1">To</label>
                <input
                  type="date"
                  className="form-control mb-2"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
                <button
                  type="button"
                  className="px-3 py-1 bg-primary text-white rounded text-12"
                  onClick={() => {
                    if (customStart && customEnd) {
                      onCustomRange(customStart, customEnd);
                      setIsOpen(false);
                    }
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(DateRangeFilter);
